import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import OperatorCard from "@/components/OperatorCard";
import ArticleCard from "@/components/ArticleCard";
import SEO from "@/components/SEO";
import { ArrowRight, Compass, ScrollText, ShieldCheck, Layers, Sparkles } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1542259009477-d625272157b7?auto=format&fit=crop&w=2400&q=80";
const TRAVEL_IMG = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1800&q=80";

const CRITERIA = [
  { k: "Inventory", desc: "Breadth of US properties — chains, indies, all star levels.", icon: Layers },
  { k: "Loyalty value", desc: "Status earnings, redemption rates, breakage, real perks.", icon: Sparkles },
  { k: "Cancellation", desc: "How forgiving each operator's standard policy actually is.", icon: ShieldCheck },
  { k: "Customer support", desc: "What happens when a booking goes sideways at 11 pm.", icon: Compass },
  { k: "Editorial trust", desc: "Transparent pricing, no shady up-sells, honest listings.", icon: ScrollText },
];

export default function Home() {
  const [operators, setOperators] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/operators").then(({ data }) => setOperators(data));
    api.get("/articles", { params: { limit: 5 } }).then(({ data }) => setArticles(data));
  }, []);

  const featuredKey = "choice";
  const featuredOp = operators.find((o) => o.key === featuredKey);
  const rest = operators.filter((o) => o.key !== featuredKey);
  const topOps = featuredOp ? [featuredOp, ...rest].slice(0, 3) : operators.slice(0, 3);

  const otas = rest.filter((o) => o.kind === "ota").slice(0, 4);
  const metas = rest.filter((o) => o.kind === "meta").slice(0, 4);
  const chains = rest.filter((o) => o.kind === "chain").slice(0, 4);

  const featuredArticle = articles.find((a) => a.featured) ?? articles[0];
  const restArticles = articles.filter((a) => a !== featuredArticle).slice(0, 3);

  return (
    <div className="bg-sand">
      <SEO
        title=""
        description="An independent editorial guide to every major US hotel booking source — Booking.com, Hotels.com, Marriott Bonvoy, Hilton Honors, Hyatt, IHG and 10 more. Pros, cons, loyalty value, scored 1-10."
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-sand via-sand/95 to-sand/20 md:via-sand/85" />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-transparent to-sand" />
        </div>
        <div className="relative container-x pt-24 pb-24 md:pt-32 md:pb-36 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8 animate-fade-up">
            <div className="overline mb-5 text-terracotta">Independent · editorial · ad-free reviews</div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-charcoal text-balance">
              Don't pick a hotel — pick the <em className="not-italic text-terracotta">operator</em> who'll get you the best room, points, and refund.
            </h1>
            <p className="mt-8 text-charcoal/80 text-lg max-w-2xl leading-relaxed">
              We score every major US booking source — OTAs, meta-search, and chain loyalty programs — on five criteria. Editorial only. No partner pays for placement.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/operators" data-testid="hero-cta-operators" className="btn-primary">
                See the 16 operators ranked <ArrowRight size={14} />
              </Link>
              <Link to="/methodology" data-testid="hero-cta-methodology" className="text-sm uppercase tracking-[0.18em] text-charcoal/70 hover:text-terracotta link-underline">
                How we score
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 gap-6 md:gap-12 max-w-md border-t border-charcoal/15 pt-6">
              {[
                { v: operators.length || "16", k: "Operators ranked" },
                { v: "5", k: "Scoring criteria" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-serif text-4xl md:text-5xl text-charcoal leading-none">{s.v}</div>
                  <div className="overline mt-2 text-charcoal/60">{s.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TOP 3 OPERATORS */}
      <section className="container-x py-20 md:py-28" data-testid="home-top-operators">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7">
            <div className="overline text-terracotta">Top of the list</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">
              The three booking sources we'd use this week.
            </h2>
          </div>
          <p className="md:col-span-5 text-charcoal/70 leading-relaxed">
            Editorial picks across categories. Click any card for the full review — strengths, watch-outs, loyalty math, and who it's actually for.
          </p>
        </div>

        {operators.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {topOps.map((op) => (
              <OperatorCard key={op.key} operator={op} variant="hero" featured={op.key === featuredKey} />
            ))}
          </div>
        )}
      </section>

      {/* CHOICE HOTELS SPOTLIGHT */}
      {featuredOp && (
        <section
          className="relative overflow-hidden"
          data-testid="home-choice-spotlight"
          style={{ background: `linear-gradient(100deg, ${featuredOp.color} 0%, ${featuredOp.color}e6 55%, #1a1a1a 110%)` }}
        >
          <div className="absolute inset-0 noise-overlay opacity-25 pointer-events-none" />
          <img src={featuredOp.image} alt="" className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-25" />
          <div className="relative container-x py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center text-sand">
            <div className="md:col-span-7">
              <div className="overline text-sand/70 flex items-center gap-3">
                <Sparkles size={14} className="text-sand" /> Editor's pick · February 2026
              </div>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight text-balance">
                Why <em className="not-italic">{featuredOp.name}</em> is our favorite way to travel America on a budget.
              </h2>
              <p className="mt-6 text-sand/85 text-lg max-w-2xl leading-relaxed">
                {featuredOp.summary}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to={`/operators/${featuredOp.key}`} data-testid="home-choice-read-more" className="inline-flex items-center gap-2 bg-sand text-charcoal hover:bg-terracotta hover:text-sand transition-colors px-6 py-3 text-sm uppercase tracking-[0.16em]">
                  Read the full review <ArrowRight size={14} />
                </Link>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/go/operator/${featuredOp.key}`}
                  target="_blank"
                  rel="noopener sponsored"
                  data-testid="home-choice-visit"
                  className="inline-flex items-center gap-2 border border-sand/40 hover:border-sand text-sand px-6 py-3 text-sm uppercase tracking-[0.16em] transition-colors"
                >
                  Visit {featuredOp.name}
                </a>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="bg-sand text-charcoal p-7 border-t-[3px]" style={{ borderTopColor: featuredOp.color }}>
                <div className="overline">Editor score</div>
                <div className="font-serif text-7xl mt-1 leading-none">{featuredOp.editor_score?.toFixed(1)}<span className="text-xl text-charcoal/40"> / 10</span></div>
                <div className="mt-5 grid grid-cols-2 gap-4 text-sm text-charcoal/75">
                  <div><div className="overline text-[10px] mb-1">Loyalty</div>{featuredOp.loyalty?.name}</div>
                  <div><div className="overline text-[10px] mb-1">Inventory</div>{featuredOp.inventory}</div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {featuredOp.best_for?.slice(0, 3).map((b) => (
                    <span key={b} className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 border border-charcoal/15">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* METHODOLOGY */}
      <section className="bg-forest text-sand relative overflow-hidden">
        <img src={TRAVEL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/95 via-forest/90 to-forest" />
        <div className="relative container-x py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="overline text-terracotta-light">How we score</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Five criteria. Twelve months of testing. One number out of ten.
            </h2>
            <p className="mt-6 text-sand/80 leading-relaxed">
              We don't run hotel deals — we run the editorial. Every operator we cover is scored against the same five-factor framework, every twelve months, with public methodology.
            </p>
            <Link to="/methodology" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-sand hover:text-terracotta-light link-underline" data-testid="methodology-cta">
              Read the full methodology <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {CRITERIA.map((c, i) => {
              const Ico = c.icon;
              return (
                <div key={c.k} className="border border-sand/15 p-6 hover:border-sand/35 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-mono text-xs text-terracotta-light">0{i + 1}</span>
                    <Ico size={16} className="text-terracotta-light" />
                  </div>
                  <div className="font-serif text-2xl">{c.k}</div>
                  <p className="mt-2 text-sm text-sand/75 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BY KIND — three editorial columns */}
      <section className="container-x py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="overline text-terracotta">Browse by kind</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Three booking-source families — three different ways to win.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {[
            { title: "OTAs", desc: "Book any hotel without a chain account.", ops: otas, hint: "Booking, Hotels.com, Expedia…" },
            { title: "Meta-search", desc: "Compare prices across every booking site.", ops: metas, hint: "Kayak, Trivago, Tripadvisor" },
            { title: "Chain loyalty", desc: "Earn points and elite perks at one brand.", ops: chains, hint: "Marriott, Hilton, Hyatt…" },
          ].map((col) => (
            <div key={col.title}>
              <div className="overline text-terracotta">{col.title}</div>
              <div className="mt-2 font-serif text-2xl text-charcoal">{col.desc}</div>
              <div className="mt-6 space-y-3">
                {col.ops.map((op) => (
                  <Link
                    key={op.key}
                    to={`/operators/${op.key}`}
                    data-testid={`home-kind-op-${op.key}`}
                    className="group flex items-center gap-4 p-4 bg-white border border-charcoal/10 hover:border-charcoal/30 transition-colors"
                  >
                    <span
                      className="w-1 self-stretch shrink-0"
                      style={{ background: op.color }}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg leading-tight">{op.name}</div>
                      <div className="text-xs text-charcoal/60 truncate">{op.tagline}</div>
                    </div>
                    <div className="font-mono text-sm text-charcoal/80">{op.editor_score?.toFixed(1)}</div>
                    <ArrowRight size={14} className="text-charcoal/40 group-hover:text-terracotta group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="bg-cream py-20 md:py-28 border-y border-charcoal/5">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-8">
              <div className="overline text-terracotta">Long reads</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                Editorial guides that actually save you money.
              </h2>
            </div>
            <p className="md:col-span-4 text-charcoal/70 leading-relaxed">
              Brand ladders, points-vs-cash math, real test itineraries. Written by US travelers, never sponsored.
            </p>
          </div>

          <div className="grid md:grid-cols-12 gap-6 md:gap-8">
            {featuredArticle && (
              <div className="md:col-span-7">
                <ArticleCard article={featuredArticle} variant="wide" />
              </div>
            )}
            <div className="md:col-span-5 space-y-6">
              {restArticles.slice(0, 2).map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/guides" className="btn-ghost" data-testid="home-cta-guides">Read every guide</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
